
import { reactive, defineComponent, computed, watch, ref } from 'vue';
import mapboxgl, { Map, LngLatBounds, LngLat, LngLatBoundsLike } from 'mapbox-gl';
import _ from 'lodash';

import getTailwindColor from '@/getTailwindColor';
import useApp from '@/composables';

import MapboxButtonControl from './MapboxButtonControl';
import MapboxGLMap, {
    Coordinates,
    Source,
    CENTER_OF_ALBERTA,
    ALBERTA_BOUNDS
} from './MapboxGLMap.vue';

import AlbertaIcon from '@/assets/img/alberta-icon.svg';

type SourceCoordinate = {
    id: string;
    title: string;
    classificationDesc: string;
    classification: string;
    currentSiteStatus: string;
    coordinates: Coordinates;
}

const MAP_VERT_PADDING = 1;
const MAP_HORZ_PADDING = 6;

const paddedBounds: LngLatBoundsLike = [
    [ALBERTA_BOUNDS[0][0] - MAP_HORZ_PADDING, ALBERTA_BOUNDS[0][1] - MAP_VERT_PADDING],
    [ALBERTA_BOUNDS[1][0] + MAP_HORZ_PADDING, ALBERTA_BOUNDS[1][1] + MAP_VERT_PADDING],
];

export default defineComponent({
    name: 'Map',
    components: {
        MapboxGLMap
    },
    setup() {
        const { state, setOpenSite } = useApp();

        const mapState: {
            mapObject: mapboxgl.Map | null,
            hoveredMarkers: Array<mapboxgl.Marker>,
            selectedMarkers: Array<mapboxgl.Marker>,
            mapPopup: mapboxgl.Popup,
            currentZoom: number
        } = reactive({
            mapObject: null,
            hoveredMarkers: [],
            selectedMarkers: [],
            mapPopup: new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false
            }),
            currentZoom: 0
        });
        const mapIsLoaded = ref(false);
        const renderMap = computed(() => (mapIsLoaded.value && state.isInit));

        const accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
        const centerCoordinates = CENTER_OF_ALBERTA;

        const mapOptions = {
            zoom: 4.35,
            minZoom: 4.35,
            maxZoom: 17,
            maxBounds: paddedBounds
        };
        const withNavigation = true;
        const MIN_CLICKABLE_ZOOM = 8;

        const filteredRecordsCount = computed(() => state.listViewSites.length.toLocaleString());
        const recordsCount = computed(() => state.allSitesList.length.toLocaleString());

        const coordinatesData = computed((): SourceCoordinate[] => {
            const coordinates: SourceCoordinate[] = state.mapViewSites.flatMap((sl) => sl.coordinates.map((c): SourceCoordinate => ({
                id: sl.id,
                title: sl.operationName,
                classificationDesc: sl.fileClassificationDescription,
                classification: sl.fileClassification.toLowerCase(),
                currentSiteStatus: sl.currentSiteStatus,
                coordinates: [c.longitude, c.latitude],
            })));
            return coordinates;
        });

        const sources = computed((): Source[] => [{
            id: 'PointsSource',
            data: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: coordinatesData.value.map((c) => ({
                        type: 'Feature',
                        properties: c,
                        geometry: {
                            type: 'Point', coordinates: c.coordinates,
                        }
                    }))
                }
            }
        }]);

        const mapBounds = computed((): LngLatBounds => {
            const _bounds = new LngLatBounds();
            coordinatesData.value.forEach((c) => {
                _bounds.extend(c.coordinates);
            });

            return _bounds;
        });

        const mapIsClickable = computed(() => mapState.mapObject && mapState.currentZoom > MIN_CLICKABLE_ZOOM);

        const layers = [
            {
                id: 'PointsLayer',
                source: 'PointsSource',
                type: 'circle',
                clickable: true,
                hoverable: true,
                paint: {
                    'circle-radius': [
                        'interpolate', ['linear'], ['zoom'],
                        (MIN_CLICKABLE_ZOOM - 0.001), 2,
                        MIN_CLICKABLE_ZOOM, 6,
                    ],
                    'circle-stroke-color': '#808080',
                    'circle-stroke-width': 0.5,
                    'circle-color': [
                        'match',
                        ['get', 'classification'],
                        'csu',
                        getTailwindColor('map.contaminated-site-upstream'),
                        'rec',
                        getTailwindColor('map.reclamation'),
                        'pst',
                        getTailwindColor('map.petroleum-storage-tank'),
                        'scd',
                        getTailwindColor('map.site-contamination-and-decomissioning'),
                        /* other */getTailwindColor('gray', 500)
                    ]
                },
            }
        ];

        const customControls = [
            new MapboxButtonControl(
                'alberta-zoom-ctrl',
                'Zoom to Alberta Bounds',
                AlbertaIcon,
                'bottom-right',
                (map: Map, e: MouseEvent) => {
                    map.fitBounds(paddedBounds, { animate: true }, e);
                }
            )
        ];

        const onMoveEnd = function (currentBounds: LngLatBounds) {
            const currentlyDisplayedSites = _.chain(state.mapViewSites)
                .filter((s) => _.reduce((s.coordinates), (acc:boolean, item) => (acc || currentBounds.contains(new LngLat(item.longitude, item.latitude))), false)).value();
            state.listViewSites = currentlyDisplayedSites;
        };

        const onMapLoaded = function (map: mapboxgl.Map) {
            mapState.mapObject = map;
            mapIsLoaded.value = true;
        };

        const onMarkerClick = function (features:any) {
            if (!mapIsClickable.value || !features.length) {
                return;
            }

            const [feature] = features;
            const { properties: { id } } = feature;
            const site = state.mapViewSites.find((x) => x.id === id);
            setOpenSite(site);
        };

        const onMarkerHover = function (f:any) {
            if (f.features && mapState.mapObject) {
                const p = f.features[0].properties;
                const innerHTML = p.title;
                mapState.mapPopup.setLngLat([f.event.lngLat.lng, f.event.lngLat.lat]).setHTML(innerHTML).addTo(mapState.mapObject);
            /* } /* else if (!mapIsClickable.value && f.features && mapState.mapObject) {
                const innerHTML = '<span class="text-xs text-gray-400 italic">Zoom in to click on site</span>';
                mapState.mapPopup.setLngLat([f.event.lngLat.lng, f.event.lngLat.lat]).setHTML(innerHTML).addTo(mapState.mapObject); */
            } else {
                mapState.mapPopup.remove();
            }
        };

        const onZoomEnd = function (zoom: number) {
            mapState.currentZoom = zoom;
        };

        watch(() => state.hoveredSite, () => {
            if (!mapState.mapObject) {
                return;
            }

            mapState.hoveredMarkers.forEach((m) => {
                m.remove();
            });

            mapState.hoveredMarkers = !state.hoveredSite ? [] : state.hoveredSite.coordinates.map((c) => {
                const mm = document.createElement('span');
                mm.setAttribute('class', `map-marker hovered ${state.hoveredSite?.fileClassification}`);
                const mk = new mapboxgl.Marker({
                    anchor: 'center',
                    color: 'black',
                    element: mm
                })
                    .setLngLat([c.longitude, c.latitude]);
                if (mapState.mapObject) {
                    mk.addTo(mapState.mapObject);
                }
                return mk;
            });
        });

        watch(() => state.openSite, () => {
            if (!mapState.mapObject) {
                return;
            }

            mapState.selectedMarkers.forEach((m) => {
                m.remove();
            });

            mapState.selectedMarkers = !state.openSite ? [] : state.openSite.coordinates.map((c) => {
                const mm = document.createElement('span');
                mm.setAttribute('class', `map-marker selected ${state.openSite?.fileClassification}`);
                const mk = new mapboxgl.Marker({
                    anchor: 'center',
                    color: 'black',
                    element: mm
                })
                    .setLngLat([c.longitude, c.latitude]);
                if (mapState.mapObject) {
                    mk.addTo(mapState.mapObject);
                }
                return mk;
            });
        });

        return {
            renderMap,
            accessToken,
            centerCoordinates,
            mapOptions,
            withNavigation,
            customControls,
            sources,
            layers,
            recordsCount,
            filteredRecordsCount,
            mapBounds,
            onMoveEnd,
            onMapLoaded,
            onMarkerClick,
            onMarkerHover,
            onZoomEnd,
            mapIsClickable
        };
    }
});
